import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { ContentType, IntegrationV2Query, ProductIdentifier } from '@/gql/graphql';
import { useParams } from 'react-router';
import { getTypedMetadata, isTikTokMetadata } from '@/routes/trackings/tracking-utils';
import { useEffect } from 'react';

import {
  FormLayout,
  FormSection,
  SectionHeader,
  RadioField,
  SectionParagraph
} from './../../components/tracking-details-form/base';
import { FormHeaderSection } from '../../components/tracking-details-form/shared-sections';
import { SaveButton } from '../../components/save-button';
import { useIntegrationStore } from '../../stores/tracking-store';
import { useApolloClient } from '@apollo/client';

const formSchema = z.object({
  tagDelivery: z.enum(['GTM', 'DIRECT']),
  transactionIdentifier: z.enum([ContentType.Product, ContentType.ProductGroup]),
  productIdentifier: z.enum([
    ProductIdentifier.ProductId,
    ProductIdentifier.ProductSku,
    ProductIdentifier.VariantId
  ])
});

export const TiktokProductSettingsTab = ({
  isSetup,
  onContinue
}: {
  isSetup: boolean;
  onContinue: () => void;
}) => {
  const { toast } = useToast();
  const params = useParams<{ id: string }>();
  const client = useApolloClient();
  const { integration, isLoading, updateMetadata } = useIntegrationStore();

  const metadata = getTypedMetadata({
    integrationV2: integration as IntegrationV2Query['integrationV2']
  });

  const tiktokMetadata = isTikTokMetadata(metadata) ? metadata : null;

  // Check if the integration is fully configured
  const isConfigured = tiktokMetadata?.contentType && tiktokMetadata.productIdentifier;
  const isInitialSetup = !isConfigured || isSetup;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tagDelivery: 'GTM',
      transactionIdentifier: ContentType.ProductGroup,
      productIdentifier: ProductIdentifier.ProductId
    },
    resetOptions: {
      keepDirtyValues: true
    }
  });

  // Reset form when data loads
  useEffect(() => {
    if (!isLoading && integration) {
      // Use nullish coalescing and type assertion
      const resetValues = {
        tagDelivery: (tiktokMetadata?.tagDelivery as 'GTM' | 'DIRECT' | null | undefined) ?? 'GTM',
        transactionIdentifier: tiktokMetadata?.contentType as
          | ContentType.Product
          | ContentType.ProductGroup,
        productIdentifier: tiktokMetadata?.productIdentifier as
          | ProductIdentifier.ProductId
          | ProductIdentifier.ProductSku
          | ProductIdentifier.VariantId
      };
      form.reset(resetValues);
    }
  }, [integration, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateMetadata(client, {
        id: params.id!,
        metadata: {
          tagDelivery: values.tagDelivery,
          contentType: values.transactionIdentifier,
          productIdentifier: values.productIdentifier
        }
      });
      toast({
        title: 'Form submitted',
        description: 'Your Tiktok integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_) {
      toast({
        title: 'Error',
        description: 'Failed to save Tiktok integration settings.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Product Settings">
            This is where you can customize how we send product data to Tiktok.
          </FormHeaderSection>
          <FormSection>
            <SectionHeader>What is your Transaction Identifier?</SectionHeader>
            <SectionParagraph>
              Most merchants use Product Group and send the Product ID as the Product Identifier.
            </SectionParagraph>
            <RadioField
              form={form}
              name="transactionIdentifier"
              options={[
                { label: 'Product Group', value: ContentType.ProductGroup },
                { label: 'Product', value: ContentType.Product }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>What is your Product Identifier?</SectionHeader>
            <SectionParagraph>
              Product ID is typically the primary identifier in Tiktok integrations, but this can be
              overridden if needed.
            </SectionParagraph>
            <RadioField
              form={form}
              name="productIdentifier"
              options={[
                { label: 'Product ID', value: ProductIdentifier.ProductId },
                { label: 'Product SKU', value: ProductIdentifier.ProductSku },
                { label: 'Variant ID', value: ProductIdentifier.VariantId }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>How should the browser pixel fire?</SectionHeader>
            <SectionParagraph>
              Through Google Tag Manager, you import Contextual&apos;s container into your own GTM
              account. Directly, Contextual fires the pixel from your storefront itself: no GTM
              account or container needed, and changes take effect within minutes.
            </SectionParagraph>
            <RadioField
              form={form}
              name="tagDelivery"
              options={[
                {
                  label: 'Through Google Tag Manager',
                  value: 'GTM'
                },
                {
                  label: 'Directly from Contextual',
                  value: 'DIRECT',
                  badge: 'No GTM needed'
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SaveButton isDirty={isDirty} isInitialSetup={isInitialSetup} isUpdating={isLoading} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};

export default TiktokProductSettingsTab;
